import { ContactFormSubmitPayload } from "api/form.type";
import { TOOL_SECTION_KEYS } from "constants/dynamicFormFields";
import type { FormValues } from "types/form.types";

const isEmptyPrimitive = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

const pruneEmptyValues = (value: unknown): unknown => {
  if (isEmptyPrimitive(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const items = value.map(pruneEmptyValues).filter((item) => item !== undefined);

    return items.length > 0 ? items : undefined;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, nestedValue]) => [key, pruneEmptyValues(nestedValue)] as const)
      .filter(([, nestedValue]) => nestedValue !== undefined);

    if (entries.length === 0) {
      return undefined;
    }

    return Object.fromEntries(entries);
  }

  return value;
};

type PhoneFormValue = {
  dialCode?: { value?: string };
  number?: string;
};

const formatPhoneValue = (phone: unknown): string | undefined => {
  if (!phone || typeof phone !== "object") {
    return typeof phone === "string" && phone.trim() !== "" ? phone.trim() : undefined;
  }

  const { dialCode, number } = phone as PhoneFormValue;
  const dialCodeValue = dialCode?.value?.trim() ?? "";
  const phoneNumber = number?.trim() ?? "";

  if (!dialCodeValue || !phoneNumber) {
    return undefined;
  }

  return `${dialCodeValue}${phoneNumber}`;
};

type SelectOptionLike = {
  name?: string;
  value?: string;
};

const flattenToolInfo = (flattened: Record<string, unknown>): Record<string, unknown> => {
  const toolInfo = flattened.toolInfo;

  const tool = (
    toolInfo && typeof toolInfo === "object" && !Array.isArray(toolInfo)
      ? (toolInfo as Record<string, unknown>).tool
      : flattened.tool
  ) as SelectOptionLike | null | undefined;

  const requestType = (
    toolInfo && typeof toolInfo === "object" && !Array.isArray(toolInfo)
      ? (toolInfo as Record<string, unknown>).requestType
      : flattened.requestType
  ) as SelectOptionLike | null | undefined;

  if (tool?.name) {
    flattened.service_tool = tool.name;
  }

  if (requestType?.name) {
    flattened.service_type = requestType.name;
  }

  delete flattened.toolInfo;
  delete flattened.tool;
  delete flattened.requestType;

  return flattened;
};

const flattenNestedSection = (flattened: Record<string, unknown>, sectionKey: string): Record<string, unknown> => {
  const section = flattened[sectionKey];

  if (!section || typeof section !== "object" || Array.isArray(section)) {
    delete flattened[sectionKey];
    return flattened;
  }

  Object.entries(section as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined) {
      flattened[key] = value;
    }
  });

  delete flattened[sectionKey];

  return flattened;
};

const flattenFormValues = (result: Record<string, unknown>): Record<string, unknown> => {
  let flattened = { ...result };

  if ("contactInfo" in flattened && flattened.contactInfo && typeof flattened.contactInfo === "object") {
    const contactInfo = flattened.contactInfo as Record<string, unknown>;

    Object.entries(contactInfo).forEach(([key, value]) => {
      if (value !== undefined) {
        flattened[key] = value;
      }
    });

    delete flattened.contactInfo;
  }

  flattened = flattenToolInfo(flattened);

  TOOL_SECTION_KEYS.forEach((sectionKey) => {
    flattened = flattenNestedSection(flattened, sectionKey);
  });

  if ("phone" in flattened) {
    const formattedPhone = formatPhoneValue(flattened.phone);

    if (formattedPhone) {
      flattened.phone = formattedPhone;
    } else {
      delete flattened.phone;
    }
  }

  return flattened;
};

/** Returns form data without empty values and without nested contact/tool sections. */
export const getFilledFormValues = (data: FormValues): ContactFormSubmitPayload => {
  const pruned = pruneEmptyValues(data);

  if (!pruned || typeof pruned !== "object" || Array.isArray(pruned)) {
    return {};
  }

  return flattenFormValues(pruned as Record<string, unknown>) as ContactFormSubmitPayload;
};

export type { ContactFormSubmitPayload } from "api/form.type";
